import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Calendar, TrendingUp, User, Image as ImageIcon, X, Filter, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HRReports() {
  const [user, setUser] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [groupedData, setGroupedData] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) {
      navigate('/hr/login');
      return;
    }
    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'hr') {
      navigate('/hr/login');
      return;
    }
    setUser(parsedUser);
    fetchEmployees();
    
    // Set default date range to last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, [navigate]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchAttendance();
    }
  }, [selectedEmployee, startDate, endDate]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/attendance/history?start_date=${startDate}&end_date=${endDate}`;
      if (selectedEmployee !== 'all') {
        url += `&employee_id=${selectedEmployee}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAttendance(response.data);
      groupAttendanceData(response.data);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const groupAttendanceData = (data) => {
    const grouped = {};
    
    data.forEach(record => {
      const employeeKey = record.employee_id;
      const date = new Date(record.timestamp).toLocaleDateString();
      
      if (!grouped[employeeKey]) {
        grouped[employeeKey] = {
          employee_name: record.employee_name,
          employee_id: record.employee_id,
          dates: {}
        };
      }
      
      if (!grouped[employeeKey].dates[date]) {
        grouped[employeeKey].dates[date] = {
          check_in: null,
          check_out: null
        };
      }
      
      if (record.attendance_type === 'check_in') {
        grouped[employeeKey].dates[date].check_in = record;
      } else {
        grouped[employeeKey].dates[date].check_out = record;
      }
    });
    
    setGroupedData(grouped);
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/attendance/export?format=csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to download report');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/hr/login');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Photo Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl w-full bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 bg-zinc-900 text-white flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-bold uppercase">{selectedPhoto.employee_name}</h3>
                <p className="text-sm text-zinc-300">
                  {selectedPhoto.attendance_type.replace('_', ' ').toUpperCase()} - {new Date(selectedPhoto.timestamp).toLocaleString()}
                </p>
              </div>
              <button 
                onClick={() => setSelectedPhoto(null)}
                className="p-2 hover:bg-zinc-800 rounded"
              >
                <X size={24} />
              </button>
            </div>
            {selectedPhoto.selfie_data ? (
              <img 
                src={selectedPhoto.selfie_data} 
                alt="Attendance selfie"
                className="w-full max-h-[70vh] object-contain"
              />
            ) : (
              <div className="w-full h-96 flex items-center justify-center bg-zinc-100">
                <p className="text-zinc-500 font-medium">Photo deleted (60+ days old)</p>
              </div>
            )}
            <div className="p-4 bg-zinc-50 border-t-2 border-zinc-100">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{selectedPhoto.location_name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border-b-2 border-zinc-100">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <img 
              src="/anmol-logo.jpg" 
              alt="Advanced Anmol Metcomp Pvt Ltd" 
              className="h-12"
            />
            <button onClick={handleLogout} className="text-sm text-zinc-600 hover:text-zinc-900 font-medium">
              Logout
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-zinc-900">Attendance History</h1>
              <p className="text-sm text-zinc-500 font-medium mt-1">{user.name}</p>
            </div>
          </div>

          <div className="flex gap-4 mt-6 flex-wrap">
            <button
              onClick={() => navigate('/hr/dashboard')}
              className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900"
            >
              Attendance
            </button>
            <button
              onClick={() => navigate('/hr/employees')}
              className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900"
            >
              Employees
            </button>
            <button
              onClick={() => navigate('/hr/locations')}
              className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900"
            >
              Locations
            </button>
            <button
              onClick={() => navigate('/hr/reports')}
              className="px-4 py-2 bg-zinc-900 text-white text-sm font-bold uppercase tracking-wider rounded-sm"
            >
              Reports
            </button>
            <button
              onClick={() => navigate('/hr/notifications')}
              className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900"
            >
              Notifications
            </button>
            <button onClick={() => navigate('/hr/tracking')} className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900" data-testid="nav-tracking-from-reports">Tracking</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="bg-white border-2 border-zinc-100 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={20} className="text-zinc-600" />
            <h2 className="font-heading text-lg font-bold uppercase text-zinc-900">Filters</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">Employee</label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="w-full h-12 px-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none"
              >
                <option value="all">All Employees</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name} ({emp.employee_id})</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full h-12 px-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full h-12 px-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none"
              />
            </div>
            
            <div className="flex items-end">
              <button
                onClick={handleExport}
                className="w-full h-12 bg-green-600 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-green-700 flex items-center justify-center gap-2"
              >
                <Download size={20} />
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Attendance Data */}
        {loading ? (
          <div className="text-center py-12 bg-white border-2 border-zinc-100 rounded-lg">
            <p className="text-zinc-500">Loading attendance records...</p>
          </div>
        ) : Object.keys(groupedData).length === 0 ? (
          <div className="text-center py-12 bg-white border-2 border-zinc-100 rounded-lg">
            <Calendar size={48} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-500 font-medium">No attendance records found</p>
            <p className="text-sm text-zinc-400 mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.values(groupedData).map((employeeData, empIdx) => (
              <div key={empIdx} className="bg-white border-2 border-zinc-100 rounded-lg overflow-hidden">
                <div className="bg-zinc-900 text-white p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <User size={20} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold uppercase">{employeeData.employee_name}</h3>
                      <p className="font-mono text-sm text-zinc-300">ID: {employeeData.employee_id}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">{Object.keys(employeeData.dates).length}</p>
                    <p className="text-xs text-zinc-300 uppercase">Days</p>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="space-y-4">
                    {Object.entries(employeeData.dates).sort((a, b) => new Date(b[0]) - new Date(a[0])).map(([date, times]) => (
                      <div key={date} className="border-2 border-zinc-100 rounded-lg p-4 hover:border-zinc-300 transition-colors">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Calendar size={16} className="text-zinc-600" />
                            <span className="font-bold text-zinc-900">{date}</span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-green-50 border-2 border-green-100 rounded p-3">
                            <p className="text-xs font-medium text-green-700 uppercase mb-2">Check In</p>
                            {times.check_in ? (
                              <>
                                <p className="font-mono text-xl font-bold text-green-900 mb-2">
                                  {new Date(times.check_in.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                                <button
                                  onClick={() => setSelectedPhoto(times.check_in)}
                                  className="w-full px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold uppercase rounded flex items-center justify-center gap-2"
                                >
                                  <ImageIcon size={16} />
                                  View Photo
                                </button>
                              </>
                            ) : (
                              <p className="text-zinc-400 text-sm">Not marked</p>
                            )}
                          </div>
                          
                          <div className="bg-blue-50 border-2 border-blue-100 rounded p-3">
                            <p className="text-xs font-medium text-blue-700 uppercase mb-2">Check Out</p>
                            {times.check_out ? (
                              <>
                                <p className="font-mono text-xl font-bold text-blue-900 mb-2">
                                  {new Date(times.check_out.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                                <button
                                  onClick={() => setSelectedPhoto(times.check_out)}
                                  className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold uppercase rounded flex items-center justify-center gap-2"
                                >
                                  <ImageIcon size={16} />
                                  View Photo
                                </button>
                              </>
                            ) : (
                              <p className="text-zinc-400 text-sm">Not marked</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
